import type { HotelsContainer } from '@elara/client-core/containers/HotelsContainer';
import type { Hotel } from '@elara/client-core/models/HotelModel';

import type { ToolResultRowEnricher } from './types.js';

import { isAggregatorBrand } from '@elara/domain/models/schemas/hotel-schemas';

import { useApp } from '#states/hooks.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { FaviconImage } from '../FaviconImage.js';

function useLoadedHotel(hotelId: string | undefined): Hotel | undefined {
  const { features } = useApp('features');
  const hotels = (features as unknown as { hotels?: HotelsContainer }).hotels;
  const handle = hotelId && hotels ? hotels.ClientHotel.get({ id: hotelId }) : undefined;
  if (!handle?.isReady || handle.error) return undefined;
  return handle.data;
}

/**
 * Hotel rows prefer the Firestore-loaded hotel's `name`, `shortAddress`, and
 * `brand.logo` for the leading icon. The loaded brand logo wins over the tool
 * output's iconUrl (the tool output occasionally lacks brand info). Falls back
 * to any iconUrl set by the extractor, then the tool's Material icon.
 */
export const hotelRowEnricher: ToolResultRowEnricher<Hotel> = {
  useModel: useLoadedHotel,
  resolveTitle: ({ model, item }) => model?.name ?? item.title,
  resolveSubtitle: ({ model, item }) => model?.shortAddress ?? model?.formattedAddress ?? item.subtitle,
  renderIcon: ({ model, item, toolIcon }) => {
    // An aggregator placeholder brand (Expedia, Booking.com, Nuitee, HRS) carries the aggregator's logo,
    // not the hotel's — never show it as the hotel's brand.
    const brandLogo = model?.brand && !isAggregatorBrand(model.brand) ? model.brand.logo : undefined;
    const logoUrl = brandLogo ?? item.iconUrl;
    if (logoUrl) return <FaviconImage src={logoUrl} />;
    return <MaterialIcon icon={toolIcon} size={IconSizes.xs} className="shrink-0 text-muted-foreground" />;
  },
};
